import { isEqual } from 'lodash';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { AnyComponent } from '@nexelem/utils';

export const MENU_DRAWER_NAME = 'MenuDrawer';

type DrawerData = {
  width: number;
  name: string;
  Component?: AnyComponent;
  props?: unknown;
};

type DrawerStoreState = {
  isChildrenProviderMounted: boolean;
  left: DrawerData;
  right: DrawerData;
  onCloseDrawer: (name: string) => void;
  onOpenDrawer: (anchor: 'left' | 'right', drawerData: DrawerData) => void;
  onToggleDrawer: (anchor: 'left' | 'right', drawerData: DrawerData) => boolean;
  onCloseAllDrawerExceptMenu: () => void;
  onCloseRightDrawer: () => void;
  onCloseAllDrawers: () => void;
  setIsChildrenProviderMounted: (value: boolean) => void;
};

const createDrawerData = (): DrawerData => {
  return {
    width: 0,
    name: '',
  };
};
export const useDrawerStore = create(
  immer<DrawerStoreState>((set, get) => {
    return {
      isChildrenProviderMounted: false,
      right: createDrawerData(),
      left: createDrawerData(),
      onCloseDrawer: (name) =>
        set((state) => {
          (['left', 'right'] as const).forEach((drawerAnchor) => {
            if (get()[drawerAnchor].name === name) {
              state[drawerAnchor].width = 0;
              state[drawerAnchor].name = '';
              state[drawerAnchor].props = undefined;
              state[drawerAnchor].Component = undefined;
            }
          });
        }),
      onOpenDrawer: (anchor, { props, name, width, Component }) =>
        set((state) => {
          state[anchor].name = name;
          state[anchor].width = width;
          state[anchor].props = props;
          state[anchor].Component = Component;
        }),
      onCloseAllDrawerExceptMenu: () =>
        set((state) => {
          if (get().right.name !== MENU_DRAWER_NAME) {
            state.right.props = undefined;
            state.right.width = 0;
            state.right.Component = undefined;
            state.right.name = '';
          }
          if (get().left.name !== MENU_DRAWER_NAME) {
            state.left.props = undefined;
            state.left.width = 0;
            state.left.Component = undefined;
            state.left.name = '';
          }
        }),
      onCloseRightDrawer: () =>
        set((state) => {
          state.right.width = 0;
          state.right.props = undefined;
          state.right.Component = undefined;
          state.right.name = '';
        }),
      onCloseAllDrawers: () =>
        set((state) => {
          state.right.width = 0;
          state.right.props = undefined;
          state.right.Component = undefined;
          state.right.name = '';

          state.left.width = 0;
          state.left.props = undefined;
          state.left.Component = undefined;
          state.left.name = '';
        }),
      onToggleDrawer: (anchor, newDrawerState) => {
        const drawerState = get()[anchor];
        const isSameDrawerName = drawerState.name === newDrawerState.name;
        const newWidthIfDataExist = isEqual(newDrawerState.props, drawerState.props) ? 0 : drawerState.width;
        const isDrawerOpen = !!drawerState.width;
        const newDrawerWidth = isDrawerOpen ? (newDrawerState.props ? newWidthIfDataExist : 0) : newDrawerState.width;
        set((state) => {
          if (isSameDrawerName) {
            state[anchor].width = newDrawerWidth;
            state[anchor].props = newDrawerState.props;
          }
          if (drawerState.name !== newDrawerState.name) {
            state[anchor].width = newDrawerState.width;
            state[anchor].name = newDrawerState.name;
            state[anchor].props = newDrawerState.props;
            state[anchor].Component = newDrawerState.Component;
          }
        });

        return isSameDrawerName ? !!newDrawerWidth : true;
      },
      setIsChildrenProviderMounted: (value) => {
        set((state) => {
          state.isChildrenProviderMounted = value;
        });
      },
    };
  }),
);

export type DrawerStore = typeof useDrawerStore;
